import { useMutation, useQueryClient } from '@tanstack/react-query';
import { agentAPI } from '../api/agent.api';
import type { StepReviewStatus, StepContent, RefusalReason } from '../types/agent.types';

export const usePlanMutations = () => {
  const queryClient = useQueryClient();

  const invalidatePlans = () => {
    queryClient.invalidateQueries({ queryKey: ['agent-plans'] });
    queryClient.invalidateQueries({ queryKey: ['agent-stats'] });
    queryClient.invalidateQueries({ queryKey: ['agent-pending-count'] });
    queryClient.invalidateQueries({ queryKey: ['agent-history'] });
  };

  const reviewStepMutation = useMutation({
    mutationFn: ({
      planId,
      stepNumber,
      review_status,
      modified_content,
      refusal_reason,
    }: {
      planId: string;
      stepNumber: number;
      review_status: StepReviewStatus;
      modified_content?: StepContent;
      refusal_reason?: RefusalReason;
    }) => agentAPI.reviewStep(planId, stepNumber, { review_status, modified_content, refusal_reason }),
    onSuccess: invalidatePlans,
  });

  const validatePlanMutation = useMutation({
    mutationFn: ({ planId, comment }: { planId: string; comment?: string }) =>
      agentAPI.validatePlan(planId, comment),
    onSuccess: invalidatePlans,
  });

  const retryPlanMutation = useMutation({
    mutationFn: (planId: string) => agentAPI.retryPlan(planId),
    onSuccess: invalidatePlans,
  });

  return {
    reviewStep: reviewStepMutation.mutateAsync,
    isReviewingStep: reviewStepMutation.isPending,
    validatePlan: validatePlanMutation.mutateAsync,
    isValidating: validatePlanMutation.isPending,
    retryPlan: retryPlanMutation.mutateAsync,
    isRetrying: retryPlanMutation.isPending,
  };
};
